import { FC } from "react";
import { ActiveScreen, ConnectionType } from "../../core/appReducer";
import { useAppState } from "../../hooks/useAppState/useAppState";
import { useChatContext } from "../../hooks/useChatContext/useChatContext";
import { usePlayerContext } from "../../hooks/usePlayerContext/usePlayerContext";
import { useVideoContext } from "../../hooks/useVideoContext/useVideoContext";
import { useSyncContext } from "../../hooks/useSyncContext/useSyncContext";
import { IntroContainer } from "../IntroContainer/IntroContainer";
import { LoadingScreen } from "./LoadingScreen/LoadingScreen";
import { ParticipantNameScreen } from "./ParticipantNameScreen/ParticipantNameScreen";
import { DeviceSelectionScreen } from "./DeviceSelectionScreen/DeviceSelectionScreen";
import { useConferenceControlContext } from "../../hooks/useConferenceControlContext/useConferenceControlContext";
import { useConferencePlaybackContext } from "../../hooks/useConferencePlaybackContext/ConferencePlaybackContext";

export const PreJoinScreens: FC = () => {
	const { connect: chatConnect } = useChatContext();
	const { connect: videoConnect } = useVideoContext();
	const { connect: playerConnect, disconnect: playerDisconnect } = usePlayerContext();
	const { connect: conferencePlaybackConnect } = useConferencePlaybackContext();
	const { connect: syncConnect, registerViewersMap, registerStreamDocument, registerViewerDocument, registerSpeakersMap, registerSpecialMap } = useSyncContext();
	const { joinStreamAsSpeaker, joinStreamAsViewer, startRoom } = useConferenceControlContext();
	const { appState, appDispatch } = useAppState();

	const connect = async (webrtc = true): Promise<void> => {

		window.addEventListener("beforeunload", (event) => {
			// Cancel the event as stated by the standard.
			event.preventDefault();
			// Chrome requires returnValue to be set.
			event.returnValue = "";
		});

		appDispatch({ type: "set-is-loading", isLoading: true });
		if (appState.participantId && appState.participantId !== null && appState.verifyToken && appState.verifyToken !== null) {
			try {
				console.log("MD-SPEAKERINVITE", appState.hasSpeakerInvite)
				if (appState.hasSpeakerInvite) {
					const { token } = await joinStreamAsSpeaker(appState.verifyToken, true);
					await videoConnect(token);
					playerDisconnect();
					appDispatch({ type: "set-connection-type", connectionType: ConnectionType.WebRTC });
					appDispatch({ type: "set-is-loading", isLoading: false });
					appDispatch({ type: "set-has-speaker-invite", hasSpeakerInvite: false });
					appDispatch({ type: "set-is-temp-speaker", isTempSpeaker: true });
					return;
				}

				if (appState.participantId && appState.participantId !== null) {
					console.log("MD", appState.participantType);
					switch (appState.participantType) {
					case "notified_admin":
					case "notified_host": {
						const { token, syncObjectNames: { viewersMap, speakersMap, streamDocument, specialMap }, chatChannelSid } = await startRoom(appState.verifyToken);
						syncConnect(token);
						conferencePlaybackConnect(token);
						if(webrtc)
						{
							appDispatch({ type: "set-connection-type", connectionType: ConnectionType.WebRTC });
							await videoConnect(token);
						}
						else
						{
							appDispatch({ type: "set-connection-type", connectionType: ConnectionType.DialIn });
							await videoConnect(token);
						}
						registerViewersMap(viewersMap);
						registerSpeakersMap(speakersMap);
						registerSpecialMap(specialMap);
						registerStreamDocument(streamDocument);

						if(chatChannelSid)
						{
							chatConnect(token, chatChannelSid);
						}
						appDispatch({ type: "set-is-temp-speaker", isTempSpeaker: false });
						break;
					}

					case "notified_speaker": {
						const { token, syncObjectNames: { viewersMap, speakersMap, streamDocument, specialMap }, chatChannelSid } = await joinStreamAsSpeaker(appState.verifyToken);
						syncConnect(token);
						if(webrtc)
						{
							appDispatch({ type: "set-connection-type", connectionType: ConnectionType.WebRTC });
							await videoConnect(token);
						}
						else
						{
							appDispatch({ type: "set-connection-type", connectionType: ConnectionType.DialIn });
							await videoConnect(token);
						}
						registerViewersMap(viewersMap);
						registerSpeakersMap(speakersMap);
						registerSpecialMap(specialMap);
						registerStreamDocument(streamDocument);
						if(chatChannelSid)
						{
							chatConnect(token, chatChannelSid);
						}
						appDispatch({ type: "set-is-temp-speaker", isTempSpeaker: false });
						break;
					}

					case "notified_viewer": {
						const { token } = await joinStreamAsViewer(appState.verifyToken);
						syncConnect(token);
						await playerConnect(token);
						registerViewerDocument(`user-${appState.participantId}`);
						appDispatch({ type: "set-is-temp-speaker", isTempSpeaker: false });
						appDispatch({ type: "set-is-participant-window-open", isParticipantWindowOpen: false });
						// chatConnect(data.token);
						break;
					}
					default: {
						console.error("Invalid User Role", appState.participantType);
						break;
					}
					}
				}
				appDispatch({ type: "set-is-loading", isLoading: false });
			} catch (error: any) {
				console.log(error);
				if (error?.body?.code === 20404) {
					console.log("Conference is not Open Yet");
					setTimeout(connect, 10000);
				} else if (error?.body?.code === 53113) {
					console.error(error);
				} else if (error.response?.data?.error?.message === "error finding room") {
					console.error({
						headline: "Error",
						message: "Event cannot be found. Please check the event name and try again.",
						variant: "error"
					});
					appDispatch({ type: "set-is-loading", isLoading: false });
				} else {
					console.error({
						headline: "Error",
						message: "There was an error while connecting to the event.",
						variant: "error"
					});
					appDispatch({ type: "set-is-loading", isLoading: false });
				}
			}
		}
	}

	return (
		<IntroContainer>
			{appState.isLoading ? (
				<LoadingScreen state={appState} />
			) : (
				{
					[ActiveScreen.ParticipantNameScreen]: <ParticipantNameScreen state={appState} dispatch={appDispatch} connect={connect} />,
					[ActiveScreen.DeviceSelectionScreen]: <DeviceSelectionScreen state={appState} dispatch={appDispatch} connect={connect} />
				}[appState.activeScreen]
			)}
		</IntroContainer>);
}
